import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Stack, Divider } from '@mui/material';
import { DrawerContent } from 'components/common/drawer/DrawerContent';
import { EntityFileDetails } from 'components/shared/EntityFileDetails';
import { formatToString } from 'utils/date-format';
import { useConfirmDialog } from 'contexts/ConfirmDialogContext';
import { useSnackbar } from 'notistack';
import { deleteQuote } from 'store/slices/marketplace';
import { useMarketplaceDrawers } from 'hooks/useMarketplaceDrawers';
import { useAsyncDispatch } from 'hooks/useAsyncDispatch';
import { CreateOutlined, DeleteOutlineOutlined, DescriptionOutlined } from '@mui/icons-material';

export const QuoteDetails = (props) => {
  const { close } = props;

  const dispatchAction = useAsyncDispatch();

  const { enqueueSnackbar } = useSnackbar();
  const { openConfirmDialog } = useConfirmDialog();
  const { openDrawerEditQuote } = useMarketplaceDrawers();

  const selectedQuote = useSelector((state) => state.marketplace.selectedQuote);
  const currentUser = useSelector((state) => state.auth.currentUser);
  const sharedUserList = useSelector((state) => state.files.shareList.value);

  const hasWriteAccess = useMemo(() => {
    if (selectedQuote?.user_id === currentUser?.id) return true;
    const userShare = sharedUserList?.find((item) => item.owner === currentUser?.id);
    return userShare?.access?.includes('WRITE');
  }, [sharedUserList, currentUser, selectedQuote]);

  const handleEditQuote = () => {
    openDrawerEditQuote();
  };

  const handleDeleteQuote = (item) => {
    if (!item) return;
    openConfirmDialog({
      title: 'Delete Quote',
      text: `You are about to delete this quote. Click 'Delete' to proceed`,
      handler: () => {
        dispatchAction(deleteQuote({ quoteSetId: item.quote_set_id, quoteId: item.id }), {
          onSuccess: () => {
            enqueueSnackbar(`Quote has been deleted`);
            close();
          },
          errorFallback: 'Unable to delete quote',
        });
      },
    });
  };

  return (
    <DrawerContent>
      <DrawerContent.Header title={selectedQuote?.name} />
      <EntityFileDetails>
        <EntityFileDetails.FileType
          file={selectedQuote}
          fileIcon={<DescriptionOutlined sx={{ fontSize: '100px' }} />}
        />
        <EntityFileDetails.Details title="Quote Details">
          {selectedQuote?.last_modified && (
            <EntityFileDetails.DetailsItem
              title="Modified"
              subtitle={formatToString(selectedQuote?.last_modified)}
            />
          )}
          {selectedQuote?.created_on && (
            <EntityFileDetails.DetailsItem
              title="Created"
              subtitle={formatToString(selectedQuote?.created_on)}
            />
          )}
        </EntityFileDetails.Details>
        <Divider />
        <Stack
          direction="column"
          spacing={1.5}
        >
          {hasWriteAccess && (
            <>
              <EntityFileDetails.ActionButton
                title={`Edit Quote`}
                icon={<CreateOutlined sx={{ fontSize: '22px' }} />}
                action={handleEditQuote}
              />
              <EntityFileDetails.ActionButton
                title={`Delete`}
                icon={<DeleteOutlineOutlined sx={{ fontSize: '22px' }} />}
                action={() => handleDeleteQuote(selectedQuote)}
              />
            </>
          )}
        </Stack>
      </EntityFileDetails>
    </DrawerContent>
  );
};
